import { PHASE } from "../../common/constants.ts";
import { DataTable } from "../components/DataTable/index.tsx";
import { MoreLinks } from "../components/MoreLinks.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { getCols } from "../../common/getCols.ts";
import { useLocal } from "../util/local.ts";
import type { View } from "../../common/types.ts";
import { wrappedMood } from "../components/Mood.tsx";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import type { DataTableRow } from "../components/DataTable/index.tsx";
import { wrappedCurrency } from "../components/wrappedCurrency.ts";

const UpcomingFreeAgents = ({
	players,
	projectedCapSpace,
	season,
	stats,
}: View<"upcomingFreeAgents">) => {
	useTitleBar({
		title: "Upcoming Free Agents",
		dropdownView: "upcoming_free_agents",
		dropdownFields: { seasonsUpcoming: season },
	});

	const {
		challengeNoRatings,
		gender,
		phase,
		season: currentSeason,
		userTid,
	} = useLocal(["challengeNoRatings", "gender", "phase", "season", "userTid"]);

	const superCols = [
		{
			title: "",
			colspan: 6 + stats.length,
		},
		{
			title: "Projected Interest",
			colspan: 2,
		},
		{
			title: "",
			colspan: phase === PHASE.RESIGN_PLAYERS ? 1 : 2,
		},
	];

	const cols = getCols([
		"Name",
		"Pos",
		"Team",
		"Age",
		"Ovr",
		"Pot",
		...stats.map((stat) => `stat:${stat}`),
		"Mood",
		"Mood",
		...(phase === PHASE.RESIGN_PLAYERS ? [] : ["Current Contract"]),
		"Projected Contract",
	]);
	cols[6 + stats.length]!.title = "Your Team";
	cols[7 + stats.length]!.title = "Current Team";

	const rows: DataTableRow[] = players.map((p) => {
		return {
			key: p.pid,
			metadata: {
				type: "player",
				pid: p.pid,
				season: currentSeason,
				playoffs: "regularSeason",
			},
			data: [
				wrappedPlayerNameLabels({
					injury: p.injury,
					jerseyNumber: p.jerseyNumber,
					pid: p.pid,
					skills: p.ratings.skills,
					defaultWatch: p.watch,
					firstName: p.firstName,
					firstNameShort: p.firstNameShort,
					lastName: p.lastName,
				}),
				p.ratings.pos,
				<a href={helpers.leagueUrl(["roster", `${p.abbrev}_${p.tid}`])}>
					{p.abbrev}
				</a>,
				p.age,
				!challengeNoRatings ? p.ratings.ovr : null,
				!challengeNoRatings ? p.ratings.pot : null,
				...stats.map((stat) => helpers.roundStat(p.stats[stat], stat)),
				wrappedMood({
					defaultType: "user",
					maxWidth: true,
					p,
				}),
				wrappedMood({
					defaultType: "current",
					maxWidth: true,
					p,
				}),
				...(phase === PHASE.RESIGN_PLAYERS
					? []
					: [wrappedCurrency(p.contract.amount, "M")]),
				wrappedCurrency(p.contractDesired.amount, "M"),
			],
			classNames: {
				"table-info": p.tid === userTid,
			},
		};
	});

	return (
		<>
			<MoreLinks type="freeAgents" page="upcoming_free_agents" />

			<p>
				Projected {season} cap space:{" "}
				{helpers.formatCurrency(projectedCapSpace / 1000, "M")}
			</p>

			<ul>
				<li>
					<b>Projected Contract</b> is the salary {helpers.pronoun(gender, "he")}{" "}
					would ask from your team. For your own players it already includes the
					discount a happy player gives to stay, larger if{" "}
					{helpers.pronoun(gender, "he")} is loyal and smaller if{" "}
					{helpers.pronoun(gender, "he")} is driven by money.
				</li>
				<li>
					<b>Your Team</b> is {helpers.pronoun(gender, "his")} interest in
					negotiating with you, or in re-signing if{" "}
					{helpers.pronoun(gender, "he")} is already yours.{" "}
					<b>Current Team</b> is {helpers.pronoun(gender, "his")} interest in
					re-signing with the club {helpers.pronoun(gender, "he")} plays for now.
				</li>
				<li>
					Interest is shown as High, Medium or Low (hover for the rough odds). It
					comes from today's mood: market size, facilities, team success, hype,
					loyalty, trades, playing time, time spent waiting in the minors while
					ready to help, and {helpers.pronoun(gender, "his")} traits. It does
					not predict future team performance.
				</li>
				<li>
					High interest is not a guarantee. A player still needs an offer{" "}
					{helpers.pronoun(gender, "he")} accepts, other teams can bid for{" "}
					{helpers.pronoun(gender, "him")} (you'll have to beat them to keep{" "}
					{helpers.pronoun(gender, "him")}), and {helpers.pronoun(gender, "his")}{" "}
					club may simply not want {helpers.pronoun(gender, "him")} back.
				</li>
			</ul>

			<DataTable
				cols={cols}
				defaultSort={[3, "desc"]}
				name="UpcomingFreeAgents"
				rows={rows}
				pagination
				superCols={superCols}
			/>
		</>
	);
};

export default UpcomingFreeAgents;
